// EntityActivity — audit-log timeline + notes composer scoped to a
// single entity.
//
// Drops into any detail page. Reads /admin/audit-log filtered by
// entityType + entityId; surfaces the same data the dedicated AuditLog
// page exposes but inline next to the entity, so "who cancelled this
// booking and when" is answered without leaving the page.
//
// Operator notes ride on the same infrastructure: a note is just an
// audit_log row with action=`<entityType>.note` and after.body. The note
// composer at the top POSTs to /admin/entity-notes; notes render
// distinctly in the timeline (full body inline) so they don't look like
// state-change rows.

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DetailSection } from '@drivecars/admin-ui';
import { RowAction } from '@/components/RowAction';
import {
  addEntityNote,
  fetchAdminAuditLog,
  fetchAdminAuditLogDetail,
  type AdminAuditLogRow,
  type EntityNoteType,
} from '@/lib/admin-api';

interface EntityActivityProps {
  /** The audit_log.entity_type value, e.g. 'bookings', 'cars', 'users'. */
  entityType: EntityNoteType;
  /** The audit_log.entity_id value — typically the numeric primary key. */
  entityId: number | string;
  /** Max rows to load — 25 covers the common "last few changes" case. */
  limit?: number;
  /** Section title; defaults to "Activity". */
  title?: string;
}

const fmtTime = (iso: string) => {
  const d = new Date(iso);
  const ms = Date.now() - d.getTime();
  if (ms < 60_000) return 'just now';
  const m = Math.floor(ms / 60_000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  if (days < 14) return `${days}d ago`;
  return d.toLocaleDateString();
};

function isNoteAction(action: string): boolean {
  return action.endsWith('.note');
}

export function EntityActivity({
  entityType,
  entityId,
  limit = 25,
  title = 'Activity',
}: EntityActivityProps) {
  const queryClient = useQueryClient();
  const [openId, setOpenId] = useState<string | null>(null);
  const [noteDraft, setNoteDraft] = useState('');

  const listKey = ['admin', 'audit-log', 'entity', entityType, entityId];

  const listQ = useQuery({
    queryKey: listKey,
    queryFn: ({ signal }) =>
      fetchAdminAuditLog(
        { entityType, entityId: String(entityId), limit },
        signal,
      ),
  });

  const detailQ = useQuery({
    queryKey: ['admin', 'audit-log', 'detail', openId],
    queryFn: ({ signal }) =>
      openId ? fetchAdminAuditLogDetail(openId, signal) : null,
    enabled: !!openId,
  });

  const noteMut = useMutation({
    mutationFn: (body: string) => addEntityNote(entityType, entityId, body),
    onSuccess: () => {
      setNoteDraft('');
      void queryClient.invalidateQueries({ queryKey: listKey });
    },
  });

  const rows = listQ.data?.rows ?? [];
  const trimmed = noteDraft.trim();

  return (
    <DetailSection title={`${title}${rows.length ? ` (${rows.length})` : ''}`}>
      {/* Notes composer */}
      <div className="mb-4 rounded-lg border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)]/40 p-3">
        <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
          Leave a note
        </div>
        <textarea
          value={noteDraft}
          onChange={(e) => setNoteDraft(e.target.value)}
          placeholder="Context for the next operator — call notes, escalations, anything you'd want a colleague to read."
          rows={2}
          maxLength={2000}
          className="block w-full resize-y rounded-md border border-[color:var(--bo-line)] bg-white px-3 py-2 text-[13px] text-[color:var(--bo-ink)] outline-none focus:border-[color:var(--bo-accent)]"
        />
        <div className="mt-2 flex items-center justify-between gap-3">
          <span className="text-[11px] tabular-nums text-[color:var(--bo-mute)]">
            {trimmed.length} / 2000
          </span>
          <div className="flex items-center gap-2">
            {noteMut.error ? (
              <span className="text-[11.5px] text-[#9f1239]">
                {(noteMut.error as Error).message}
              </span>
            ) : null}
            <RowAction
              kind="primary"
              busy={noteMut.isPending}
              disabled={!trimmed || trimmed.length > 2000}
              onClick={() => noteMut.mutate(trimmed)}
            >
              Add note
            </RowAction>
          </div>
        </div>
      </div>

      {listQ.error ? (
        <div className="text-[12.5px] text-[#9f1239]">
          Couldn&apos;t load activity: {(listQ.error as Error).message}
        </div>
      ) : null}

      {listQ.isLoading ? (
        <div className="text-[12.5px] text-[color:var(--bo-mute)]">
          Loading activity…
        </div>
      ) : rows.length === 0 ? (
        <div className="text-[12.5px] text-[color:var(--bo-mute)]">
          No audited mutations recorded for this {entityType.replace(/s$/, '')}.
        </div>
      ) : (
        <ol className="relative ms-3 space-y-3 border-s border-[color:var(--bo-line)] ps-5">
          {rows.map((row) => (
            <li key={row.id}>
              <ActivityRow
                row={row}
                expanded={openId === row.id}
                onToggle={() => setOpenId((id) => (id === row.id ? null : row.id))}
                detail={openId === row.id ? detailQ.data : null}
                detailLoading={openId === row.id && detailQ.isLoading}
                detailError={
                  openId === row.id && detailQ.error
                    ? (detailQ.error as Error).message
                    : null
                }
              />
            </li>
          ))}
        </ol>
      )}
    </DetailSection>
  );
}

interface ActivityRowDetail {
  action: string;
  before: unknown;
  after: unknown;
}

function ActivityRow({
  row,
  expanded,
  onToggle,
  detail,
  detailLoading,
  detailError,
}: {
  row: AdminAuditLogRow;
  expanded: boolean;
  onToggle: () => void;
  detail: ActivityRowDetail | null | undefined;
  detailLoading: boolean;
  detailError: string | null;
}) {
  const note = isNoteAction(row.action);

  // For notes, we want the body visible by default — the whole point of
  // a note is the text. Auto-fetch the detail when this row mounts so the
  // body is there without a click. (Cheap query — 100 audit rows × N
  // notes × one row each.)
  const noteDetailQ = useQuery({
    queryKey: ['admin', 'audit-log', 'detail', row.id],
    queryFn: ({ signal }) => fetchAdminAuditLogDetail(row.id, signal),
    enabled: note,
  });

  if (note) {
    const body =
      ((noteDetailQ.data?.after ?? null) as { body?: string } | null)?.body ??
      '';
    return (
      <>
        <span className="absolute -ms-[5.5px] mt-1.5 inline-block h-2 w-2 rounded-full bg-[#f59e0b]" />
        <div className="rounded-md border border-[#fde68a] bg-[#fefce8] px-3 py-2.5">
          <div className="mb-1 flex items-baseline justify-between gap-3 text-[11px]">
            <span className="font-semibold uppercase tracking-[0.08em] text-[#854d0e]">
              Note · {row.actorEmail ?? 'system'}
            </span>
            <span className="tabular-nums text-[#854d0e]/80">
              {fmtTime(row.createdAt)}
            </span>
          </div>
          {noteDetailQ.isLoading ? (
            <div className="text-[12px] text-[color:var(--bo-mute)]">Loading…</div>
          ) : (
            <div className="whitespace-pre-wrap text-[13px] text-[color:var(--bo-ink)]">
              {body || '(no body)'}
            </div>
          )}
        </div>
      </>
    );
  }

  return (
    <>
      <span className="absolute -ms-[5.5px] mt-1.5 inline-block h-2 w-2 rounded-full bg-[color:var(--bo-accent)]" />
      <button
        type="button"
        onClick={onToggle}
        className="w-full text-start"
        aria-expanded={expanded}
      >
        <div className="flex items-baseline justify-between gap-3">
          <div className="min-w-0 flex-1">
            <span className="font-mono text-[12px] font-semibold text-[color:var(--bo-ink)]">
              {row.action}
            </span>
            <span className="ms-2 text-[11.5px] text-[color:var(--bo-mute)]">
              by {row.actorEmail ?? 'system'}
            </span>
          </div>
          <span className="shrink-0 text-[11.5px] tabular-nums text-[color:var(--bo-mute)]">
            {fmtTime(row.createdAt)}
          </span>
        </div>
      </button>
      {expanded ? (
        <div className="mt-2 space-y-2 rounded-md border border-[color:var(--bo-line)] bg-[color:var(--bo-bg)] p-3 text-[11.5px]">
          {detailLoading ? (
            <div className="text-[color:var(--bo-mute)]">Loading…</div>
          ) : detailError ? (
            <div className="text-[#9f1239]">Failed: {detailError}</div>
          ) : detail ? (
            <>
              <DiffPane label="Before" value={detail.before} />
              <DiffPane label="After" value={detail.after} />
            </>
          ) : null}
        </div>
      ) : null}
    </>
  );
}

function DiffPane({ label, value }: { label: string; value: unknown }) {
  return (
    <div>
      <div className="mb-1 text-[10px] uppercase tracking-[0.1em] text-[color:var(--bo-mute)]">
        {label}
      </div>
      <pre className="overflow-x-auto whitespace-pre-wrap text-[11px]">
        {value ? JSON.stringify(value, null, 2) : '—'}
      </pre>
    </div>
  );
}
